import {
  AppWindow, BellRing, Bot, Box, Camera, Car, CreditCard, FileKey2, Folder, Globe, GraduationCap, KeyRound, Laptop, LayoutDashboard,
  Lock, Mail, Monitor, Network, Printer, Radar, Receipt, Router, Server, Share2, Smartphone, StickyNote, Tablet,
  Users, Vault, Wifi, type LucideIcon,
} from 'lucide-react'
import type {
  GeheimSoort, ItemSoort, ItemStatus, Niveau, Prioriteit, SysteemSoort, TicketBron, TicketCategorie, TicketSoort, TicketStatus,
} from '../../gedeeld/types'

/* ===========================================================================
 *  Hoe alles heet
 *
 *  Eén plek, en de sleutels zijn die van de checks in migratie 0165. Een
 *  kleur is een van de namen uit theme.css (t-ok, t-warn, ...).
 * =========================================================================== */

export interface Keuze<T extends string> { key: T; label: string; kleur?: string }

const vind = <T extends string>(lijst: Keuze<T>[], k: T | null | undefined) => lijst.find((x) => x.key === k)

/* ------------------------------------------------------------------ *
 *  Tickets
 * ------------------------------------------------------------------ */

export const TICKET_STATUS: Keuze<TicketStatus>[] = [
  { key: 'nieuw', label: 'Nieuw', kleur: 'info' },
  { key: 'bezig', label: 'In behandeling', kleur: 'brand' },
  { key: 'wacht_melder', label: 'Wacht op melder', kleur: 'oranje' },
  { key: 'wacht_extern', label: 'Wacht op leverancier', kleur: 'paars' },
  { key: 'opgelost', label: 'Opgelost', kleur: 'ok' },
  { key: 'gesloten', label: 'Gesloten', kleur: 'ok' },
]
export const ticketStatus = (s: TicketStatus) => vind(TICKET_STATUS, s) ?? TICKET_STATUS[0]!
export const isAfgerond = (s: TicketStatus) => s === 'opgelost' || s === 'gesloten'

export const TICKET_SOORT: Keuze<TicketSoort>[] = [
  { key: 'storing', label: 'Iets doet het niet' },
  { key: 'vraag', label: 'Een vraag' },
  { key: 'aanvraag', label: 'Iets aanvragen' },
  { key: 'wijziging', label: 'Iets veranderen' },
]
export const ticketSoort = (s: TicketSoort) => vind(TICKET_SOORT, s)?.label ?? s

export const CATEGORIE: Keuze<TicketCategorie>[] = [
  { key: 'hardware', label: 'Laptop, pc of scherm' },
  { key: 'telefoon', label: 'Telefoon of tablet' },
  { key: 'printer', label: 'Printer' },
  { key: 'netwerk', label: 'Internet of wifi' },
  { key: 'account', label: 'Inloggen of wachtwoord' },
  { key: 'mail', label: 'Mail' },
  { key: 'software', label: 'Programma of app' },
  { key: 'toegang', label: 'Toegang tot een map of systeem' },
  { key: 'beveiliging', label: 'Beveiliging (verdachte mail, kwijt apparaat)' },
  { key: 'overig', label: 'Iets anders' },
]
export const categorie = (c: TicketCategorie) => vind(CATEGORIE, c)?.label ?? c

export const PRIORITEIT: (Keuze<Prioriteit> & { uitleg: string })[] = [
  { key: 'laag', label: 'Laag', kleur: 'ok', uitleg: 'Kan wachten' },
  { key: 'normaal', label: 'Normaal', kleur: 'info', uitleg: 'Deze week' },
  { key: 'hoog', label: 'Hoog', kleur: 'oranje', uitleg: 'Ik kan slecht verder' },
  { key: 'spoed', label: 'Spoed', kleur: 'danger', uitleg: 'De vestiging ligt stil' },
]
export const prioriteit = (p: Prioriteit) => PRIORITEIT.find((x) => x.key === p) ?? PRIORITEIT[1]!

export const ticketNummer = (n: number) => `IT-${String(n).padStart(4, '0')}`

/**
 * Waar een ticket vandaan kwam.
 *
 * Eén tabel, want de bron staat op drie plekken: het icoontje in de lijst,
 * de regel onder "Gemeld" in het ticket, en de filterpillen voor IT. Stond
 * het overal los, dan krijgt een nieuwe bron stilzwijgend het woord van de
 * laatste tak van een if -- 'dashboard' was zo in het ticket "door de
 * bewaking" gaan heten, want daar eindigde de rij ifs op.
 *
 * 'scherm' heeft met opzet geen icoon: dat is de gewone weg, en een plaatje
 * achter elk ticket zegt dan niets meer. 'label' is hoe de filterpil heet,
 * 'hoe' hoe je het in een zin zegt ("gemeld ... uit het dashboard").
 */
export const BRON: (Keuze<TicketBron> & { icoon: LucideIcon | null; hoe: string })[] = [
  { key: 'scherm', label: 'Hier gemeld', icoon: null, hoe: 'via dit scherm' },
  { key: 'dashboard', label: 'Uit het dashboard', icoon: LayoutDashboard, hoe: 'uit het dashboard' },
  /* Een suggestie over een les (0238). Niet 'dashboard': wie daarop filtert
     zoekt een scherm, en een opmerking over de arbeidstijdenwet heeft er geen. */
  { key: 'academy', label: 'Uit de academie', icoon: GraduationCap, hoe: 'uit de academie' },
  { key: 'mail', label: 'Per mail', icoon: Mail, hoe: 'per mail' },
  { key: 'cowork', label: 'Via Cowork', icoon: Bot, hoe: 'via Cowork' },
  { key: 'bewaking', label: 'Door de bewaking', icoon: Radar, hoe: 'door de bewaking' },
]
export const ticketBron = (b: TicketBron) => BRON.find((x) => x.key === b) ?? BRON[0]!

/* ------------------------------------------------------------------ *
 *  Rollen (voor delen)
 * ------------------------------------------------------------------ */

export const ROLLEN: Keuze<string>[] = [
  { key: 'employee', label: 'Medewerkers' },
  { key: 'supervisor', label: 'Vestigingsleiders' },
  { key: 'technician', label: 'Technici' },
  { key: 'administratie', label: 'Administratie' },
  { key: 'management', label: 'Management' },
  { key: 'developer', label: 'Ontwikkelaars' },
  { key: 'trucksupply', label: 'Trucksupply' },
]
export const rolLabel = (r: string) => vind(ROLLEN, r)?.label ?? r

/* ------------------------------------------------------------------ *
 *  De kluis
 * ------------------------------------------------------------------ */

export const GEHEIM_SOORT: (Keuze<GeheimSoort> & { icoon: LucideIcon; waarde: string; meervoud: string })[] = [
  { key: 'login', label: 'Inlog', meervoud: 'Inloggegevens', icoon: KeyRound, waarde: 'Wachtwoord' },
  { key: 'wifi', label: 'Wifi', meervoud: 'Wifi', icoon: Wifi, waarde: 'Wachtwoord' },
  { key: 'kluis', label: 'Kluiscode', meervoud: 'Kluizen', icoon: Vault, waarde: 'Code' },
  { key: 'alarm', label: 'Alarmcode', meervoud: 'Alarmen', icoon: BellRing, waarde: 'Code' },
  { key: 'pincode', label: 'Pincode', meervoud: 'Pincodes', icoon: CreditCard, waarde: 'Pincode' },
  { key: 'licentie', label: 'Licentie', meervoud: 'Licenties', icoon: FileKey2, waarde: 'Licentiesleutel' },
  { key: 'notitie', label: 'Geheime notitie', meervoud: 'Notities', icoon: StickyNote, waarde: 'Inhoud' },
  { key: 'overig', label: 'Overig', meervoud: 'Overig', icoon: Lock, waarde: 'Waarde' },
]
export const geheimSoort = (s: GeheimSoort) => GEHEIM_SOORT.find((x) => x.key === s) ?? GEHEIM_SOORT[0]!

/** Wat het gebruikersnaam-veld bij deze soort heet. */
export function naamVeld(s: GeheimSoort): string | null {
  switch (s) {
    case 'login': return 'Gebruikersnaam of e-mail'
    case 'wifi': return 'Netwerknaam (SSID)'
    case 'kluis': case 'alarm': return 'Waar (plek, gebruiker of gebied)'
    case 'pincode': return 'Van welke pas of welk apparaat'
    case 'licentie': return 'Product'
    default: return null
  }
}

/* ------------------------------------------------------------------ *
 *  De inventaris
 * ------------------------------------------------------------------ */

export type VeldSoort = 'tekst' | 'datum' | 'getal' | 'url' | 'lang'

export interface VeldDef {
  sleutel: string
  label: string
  soort?: VeldSoort
  hulp?: string
  voorbeeld?: string
}

export type ItemGroep = 'apparaten' | 'sleutels' | 'autos' | 'abonnementen'

export const ITEM_GROEPEN: { key: ItemGroep; label: string; icoon: LucideIcon; uitleg: string }[] = [
  { key: 'apparaten', label: 'Apparaten', icoon: Laptop, uitleg: 'Laptops, pc\'s, iPads, telefoons, printers en het netwerk' },
  { key: 'sleutels', label: 'Sleutels en passen', icoon: KeyRound, uitleg: 'Wie welke sleutel, tankpas of toegangspas heeft' },
  { key: 'autos', label: 'Auto\'s', icoon: Car, uitleg: 'De auto\'s van de zaak: wie erin rijdt, APK, lease' },
  { key: 'abonnementen', label: 'Abonnementen', icoon: Receipt, uitleg: 'Licenties, domeinen, internet en telefonie' },
]

const APPLE = [
  { sleutel: 'account', label: 'Apple- of Google-account', hulp: 'Het account waarmee hij in Zoek mijn staat. Het wachtwoord hoort in de kluis, gekoppeld aan dit apparaat.', voorbeeld: 'ipad.venlo@truckwash1group.nl' },
  { sleutel: 'zoek_mijn_naam', label: 'Naam in Zoek mijn', hulp: 'Zoals hij in Zoek mijn (of Find Hub) heet, zodat je hem daar meteen vindt.', voorbeeld: 'iPad Venlo kassa' },
  { sleutel: 'mdm', label: 'Beheerd via (MDM)', voorbeeld: 'Apple Business Manager / Intune / niet' },
  { sleutel: 'imei', label: 'IMEI' },
  { sleutel: 'telefoonnummer', label: 'Telefoonnummer' },
  { sleutel: 'simkaart', label: 'Simkaart (ICCID)' },
  { sleutel: 'besturingssysteem', label: 'Versie', voorbeeld: 'iPadOS 26' },
  { sleutel: 'waarvoor', label: 'Waarvoor', voorbeeld: 'Kassa, inchecken, rooster' },
] satisfies VeldDef[]

const COMPUTER = [
  { sleutel: 'hostname', label: 'Computernaam', voorbeeld: 'TW-KANTOOR-01' },
  { sleutel: 'besturingssysteem', label: 'Besturingssysteem', voorbeeld: 'Windows 11 Pro' },
  { sleutel: 'processor', label: 'Processor' },
  { sleutel: 'geheugen', label: 'Geheugen', voorbeeld: '16 GB' },
  { sleutel: 'opslag', label: 'Opslag', voorbeeld: '512 GB SSD' },
  { sleutel: 'mac_adres', label: 'MAC-adres' },
  { sleutel: 'ip_adres', label: 'IP-adres' },
  { sleutel: 'versleuteling', label: 'Versleuteling', hulp: 'BitLocker of FileVault aan? De herstelsleutel hoort in de kluis, gekoppeld aan dit apparaat.' },
] satisfies VeldDef[]

const NETWERK = [
  { sleutel: 'soort_apparaat', label: 'Wat', voorbeeld: 'Router, switch, access point, firewall' },
  { sleutel: 'ip_adres', label: 'IP-adres' },
  { sleutel: 'mac_adres', label: 'MAC-adres' },
  { sleutel: 'beheer_url', label: 'Beheerpagina', soort: 'url' },
  { sleutel: 'provider', label: 'Provider', voorbeeld: 'KPN, Ziggo, ...' },
  { sleutel: 'klantnummer', label: 'Klantnummer bij de provider' },
  { sleutel: 'firmware', label: 'Firmware' },
] satisfies VeldDef[]

export const ITEM_SOORT: { key: ItemSoort; label: string; meervoud: string; groep: ItemGroep; icoon: LucideIcon; velden: VeldDef[] }[] = [
  { key: 'laptop', label: 'Laptop', meervoud: 'Laptops', groep: 'apparaten', icoon: Laptop, velden: COMPUTER },
  { key: 'desktop', label: 'Pc', meervoud: 'Pc\'s', groep: 'apparaten', icoon: Monitor, velden: COMPUTER },
  { key: 'tablet', label: 'Tablet / iPad', meervoud: 'Tablets', groep: 'apparaten', icoon: Tablet, velden: APPLE },
  { key: 'telefoon', label: 'Telefoon', meervoud: 'Telefoons', groep: 'apparaten', icoon: Smartphone, velden: [...APPLE, { sleutel: 'abonnement', label: 'Abonnement' }] },
  { key: 'monitor', label: 'Beeldscherm', meervoud: 'Beeldschermen', groep: 'apparaten', icoon: Monitor, velden: [
    { sleutel: 'formaat', label: 'Formaat', voorbeeld: '27"' }, { sleutel: 'aansluiting', label: 'Aansluiting', voorbeeld: 'USB-C, HDMI' }] },
  { key: 'printer', label: 'Printer', meervoud: 'Printers', groep: 'apparaten', icoon: Printer, velden: [
    { sleutel: 'ip_adres', label: 'IP-adres' }, { sleutel: 'mac_adres', label: 'MAC-adres' },
    { sleutel: 'toner', label: 'Toner of inkt', voorbeeld: 'HP 207A' }, { sleutel: 'beheer_url', label: 'Beheerpagina', soort: 'url' }] },
  { key: 'netwerk', label: 'Netwerk', meervoud: 'Netwerk', groep: 'apparaten', icoon: Router, velden: NETWERK },
  { key: 'camera', label: 'Camera', meervoud: 'Camera\'s', groep: 'apparaten', icoon: Camera, velden: [
    { sleutel: 'ip_adres', label: 'IP-adres' }, { sleutel: 'beheer_url', label: 'Beheerpagina', soort: 'url' },
    { sleutel: 'kanaal', label: 'Kanaal op de recorder' }] },
  { key: 'randapparaat', label: 'Randapparaat', meervoud: 'Randapparatuur', groep: 'apparaten', icoon: Box, velden: [
    { sleutel: 'soort_apparaat', label: 'Wat', voorbeeld: 'Dockingstation, headset, scanner, pinautomaat' }] },
  { key: 'overig', label: 'Overig', meervoud: 'Overig', groep: 'apparaten', icoon: Box, velden: [] },
  { key: 'sleutel', label: 'Sleutel', meervoud: 'Sleutels', groep: 'sleutels', icoon: KeyRound, velden: [
    { sleutel: 'opent', label: 'Waar is hij van', voorbeeld: 'Voordeur, technische ruimte, kluis' },
    { sleutel: 'exemplaren', label: 'Hoeveel exemplaren er zijn', soort: 'getal' },
    { sleutel: 'sleutelkast', label: 'Haak of vak in de sleutelkast' }] },
  { key: 'pas', label: 'Pas', meervoud: 'Passen', groep: 'sleutels', icoon: CreditCard, velden: [
    { sleutel: 'soort_pas', label: 'Wat voor pas', voorbeeld: 'Tankpas, toegangspas, bankpas, parkeerpas' },
    { sleutel: 'pasnummer', label: 'Pasnummer' },
    { sleutel: 'uitgever', label: 'Van', voorbeeld: 'Shell, Travelcard, ...' },
    { sleutel: 'geldig_tot', label: 'Geldig tot', soort: 'datum' },
    { sleutel: 'limiet', label: 'Limiet' }] },
  { key: 'auto', label: 'Auto', meervoud: 'Auto\'s', groep: 'autos', icoon: Car, velden: [
    { sleutel: 'kenteken', label: 'Kenteken', voorbeeld: 'AB-123-C', hulp: 'Met "Ophalen bij de RDW" komen merk, type, APK en de rest er vanzelf bij. Elke week haalt IT de APK opnieuw op.' },
    { sleutel: 'apk_tot', label: 'APK tot', soort: 'datum' },
    { sleutel: 'brandstof', label: 'Brandstof', voorbeeld: 'Diesel, elektrisch' },
    { sleutel: 'kleur', label: 'Kleur' },
    { sleutel: 'eerste_toelating', label: 'Eerste toelating', soort: 'datum' },
    { sleutel: 'catalogusprijs', label: 'Catalogusprijs (€)', soort: 'getal', hulp: 'Voor de bijtelling.' },
    { sleutel: 'wam_verzekerd', label: 'WAM-verzekerd (RDW)' },
    { sleutel: 'rdw_terugroep', label: 'Openstaande terugroepactie (RDW)' },
    { sleutel: 'lease_maatschappij', label: 'Lease bij' },
    { sleutel: 'lease_einde', label: 'Lease loopt tot', soort: 'datum' },
    { sleutel: 'kilometerstand', label: 'Kilometerstand', soort: 'getal' },
    { sleutel: 'km_datum', label: 'Stand van', soort: 'datum' },
    { sleutel: 'verzekering', label: 'Verzekerd bij' },
    { sleutel: 'verzekerd_tot', label: 'Verzekerd tot', soort: 'datum' },
    { sleutel: 'tankpas', label: 'Tankpas', hulp: 'Of zet de tankpas als eigen pas in de inventaris; dan staat de pincode er in de kluis bij.' },
    { sleutel: 'onderhoud', label: 'Volgend onderhoud', soort: 'datum' }] },
  { key: 'abonnement', label: 'Abonnement', meervoud: 'Abonnementen', groep: 'abonnementen', icoon: Receipt, velden: [
    { sleutel: 'account', label: 'Account', voorbeeld: 'it@truckwash1group.nl' },
    { sleutel: 'portaal', label: 'Portaal', soort: 'url' },
    { sleutel: 'kosten_per_maand', label: 'Kosten per maand (€)', soort: 'getal' },
    { sleutel: 'aantal', label: 'Aantal licenties', soort: 'getal' },
    { sleutel: 'verlengt_op', label: 'Wordt verlengd op', soort: 'datum' },
    { sleutel: 'opzegtermijn', label: 'Opzegtermijn', voorbeeld: '1 maand' },
    { sleutel: 'contractnummer', label: 'Contract- of klantnummer' },
    { sleutel: 'betaalwijze', label: 'Betaald via', voorbeeld: 'Creditcard, factuur' }] },
]

export const itemSoort = (s: ItemSoort) => ITEM_SOORT.find((x) => x.key === s) ?? ITEM_SOORT.find((x) => x.key === 'overig')!
export const soortenVan = (g: ItemGroep) => ITEM_SOORT.filter((s) => s.groep === g)

export const ITEM_STATUS: Keuze<ItemStatus>[] = [
  { key: 'in_gebruik', label: 'In gebruik', kleur: 'ok' },
  { key: 'op_voorraad', label: 'Op voorraad', kleur: 'info' },
  { key: 'reparatie', label: 'In reparatie', kleur: 'oranje' },
  { key: 'kwijt', label: 'Kwijt', kleur: 'danger' },
  { key: 'afgeschreven', label: 'Afgeschreven', kleur: 'paars' },
]
export const itemStatus = (s: ItemStatus) => vind(ITEM_STATUS, s) ?? ITEM_STATUS[0]!

/** De ene regel die zegt welke het is: kenteken, IMEI, serienummer. */
export function itemKenmerk(i: { soort: ItemSoort; velden: Record<string, string>; serienummer: string | null; code: string | null }): string | null {
  if (i.soort === 'auto') return i.velden.kenteken ?? null
  if (i.soort === 'sleutel') return i.velden.opent ?? i.code
  if (i.soort === 'pas') return [i.velden.soort_pas, i.velden.pasnummer].filter(Boolean).join(' · ') || null
  if (i.soort === 'abonnement') return [i.velden.account, i.velden.kosten_per_maand ? `€ ${i.velden.kosten_per_maand}/mnd` : null].filter(Boolean).join(' · ') || null
  return i.code ?? i.serienummer ?? null
}

/* ------------------------------------------------------------------ *
 *  Toegang
 * ------------------------------------------------------------------ */

export const SYSTEEM_SOORT: (Keuze<SysteemSoort> & { icoon: LucideIcon })[] = [
  { key: 'map', label: 'Gedeelde map', icoon: Folder },
  { key: 'sharepoint', label: 'SharePoint', icoon: Share2 },
  { key: 'teams', label: 'Teams', icoon: Users },
  { key: 'mailbox', label: 'Gedeelde mailbox', icoon: Mail },
  { key: 'app', label: 'Programma of app', icoon: AppWindow },
  { key: 'website', label: 'Website of portaal', icoon: Globe },
  { key: 'groep', label: 'Groep', icoon: Network },
  { key: 'overig', label: 'Overig', icoon: Server },
]
export const systeemSoort = (s: SysteemSoort) => SYSTEEM_SOORT.find((x) => x.key === s) ?? SYSTEEM_SOORT[7]!

export const NIVEAU: (Keuze<Niveau> & { uitleg: string })[] = [
  { key: 'lezen', label: 'Lezen', kleur: 'info', uitleg: 'Kan kijken, niets veranderen' },
  { key: 'bewerken', label: 'Bewerken', kleur: 'brand', uitleg: 'Kan toevoegen en aanpassen' },
  { key: 'beheer', label: 'Beheer', kleur: 'paars', uitleg: 'Kan ook bepalen wie erbij mag' },
]
export const niveau = (n: Niveau) => NIVEAU.find((x) => x.key === n) ?? NIVEAU[0]!

/* ------------------------------------------------------------------ *
 *  Het logboek
 * ------------------------------------------------------------------ */

const WAT: Record<string, string> = {
  aangemaakt: 'aangemaakt',
  gewijzigd: 'gewijzigd',
  verwijderd: 'verwijderd',
  uitgegeven: 'uitgegeven',
  ingeleverd: 'ingeleverd',
  status: 'status veranderd',
  verplaatst: 'verplaatst',
  gedeeld: 'gedeeld met',
  niet_meer_gedeeld: 'niet meer gedeeld met',
  toegang_gegeven: 'toegang gegeven aan',
  toegang_ingetrokken: 'toegang ingetrokken van',
  toegang_gewijzigd: 'toegang veranderd',
  bekeken: 'code bekeken',
  gekopieerd: 'code gekopieerd',
  notitie_bekeken: 'geheime notitie bekeken',
  notitie_gekopieerd: 'geheime notitie gekopieerd',
  '2fa_bekeken': '2FA-code bekeken',
  waarde_gezet: 'code opgeslagen',
  waarde_gewijzigd: 'code gewijzigd',
  moet_wijzigen: 'gemarkeerd: moet gewijzigd',
  uit_dienst: 'uit dienst afgehandeld',
  in_dienst: 'laten beginnen',
  veel_bekeken: 'bekeek veel codes achter elkaar',
}
export const watLabel = (w: string) => WAT[w] ?? w.replace(/_/g, ' ')

/** Is dit een regel over iemand die een code zag? Die vallen op in het logboek. */
export const isInzage = (w: string) => ['bekeken', 'gekopieerd', 'notitie_bekeken', 'notitie_gekopieerd', '2fa_bekeken'].includes(w)

/* ------------------------------------------------------------------ *
 *  Een IMEI nakijken
 *
 *  Casper vroeg of er een open, gratis bron is die bij een IMEI het toestel
 *  geeft. Die is er niet: de database van de GSMA is betaald, en de open
 *  lijst van Osmocom houdt rond 2016 op. Wat wel kan: het laatste cijfer is
 *  een controlecijfer (Luhn). Een overgetikt of vergeten cijfer valt zo op.
 * ------------------------------------------------------------------ */

export function imeiKlopt(imei: string): boolean | null {
  const c = imei.replace(/[\s-]/g, '')
  if (!c) return null
  if (!/^\d{15}$/.test(c)) return false
  let som = 0
  for (let n = 0; n < 15; n++) {
    let d = Number(c[n])
    if (n % 2 === 1) { d *= 2; if (d > 9) d -= 9 }
    som += d
  }
  return som % 10 === 0
}

/** De reactietijd van een ticket in woorden: "nog 40 min", "2 uur te laat", "opgepakt na 12 min". */
export function reactie(t: { reactieVoor: number | null; opgepaktAt: number | null; aangemaakt: number; status: string }):
  { tekst: string; kleur: string } | null {
  if (t.opgepaktAt) {
    const m = Math.round((t.opgepaktAt - t.aangemaakt) / 60000)
    return { tekst: `opgepakt na ${m < 60 ? `${m} min` : m < 1440 ? `${Math.round(m / 60)} uur` : `${Math.round(m / 1440)} dagen`}`, kleur: 'ok' }
  }
  if (!t.reactieVoor || t.status === 'opgelost' || t.status === 'gesloten') return null
  const over = Math.round((t.reactieVoor - Date.now()) / 60000)
  const duur = (m: number) => (m < 60 ? `${m} min` : m < 1440 ? `${Math.round(m / 60)} uur` : `${Math.round(m / 1440)} dagen`)
  if (over < 0) return { tekst: `${duur(-over)} te laat`, kleur: 'danger' }
  return { tekst: `nog ${duur(over)}`, kleur: over < 60 ? 'oranje' : 'info' }
}
